import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { cn } from "@/utils/cn";

interface DialogueBoxProps {
    speaker?: string | null;
    speakerPortraitSrc?: string | null;
    text: string;
    mood?: string;
    /** (레거시) 이전 버전의 타자기 속도. 현재는 사용하지 않고 본문을 한 번에 표시. */
    tickMs?: number;
    /** 클릭·스페이스로 다음으로 이동 가능. */
    skippable?: boolean;
    /** 렌더 직후 호출. (이전에는 타자기 종료 시점, 현재는 mount 시점) */
    onComplete?: () => void;
    className?: string;
}

/**
 * 다이얼로그/나레이션 박스. NarrativeBeat 의 dialogue / narration / action 모두 이걸로 렌더.
 *
 * 2026-04-21: 타자기(글자별 스트림) 효과 제거 — 가독성을 해친다는 피드백 반영.
 * 대신 전체 문단이 짧은 fade-in 후 한 번에 보인다. `onComplete` 는 mount 직후 호출되어
 * 기존 "다음 버튼 노출" 타이밍을 그대로 유지한다 (beatDone=true).
 */
export function DialogueBox({
    speaker,
    speakerPortraitSrc,
    text,
    mood: _mood,   // 내부 태그 — UI 에 그리지 않음. prop 은 유지 (추후 시각 변이 훅).
    skippable: _skippable = true,
    onComplete,
    className,
}: DialogueBoxProps) {
    const [hidePortrait, setHidePortrait] = useState(false);
    useEffect(() => {
        // 렌더 직후 완료 이벤트 한 번만 — 부모의 "다음" UI 노출 트리거.
        onComplete?.();
        // onComplete 식별자는 부모가 매 렌더 새로 만들 수 있으므로 text 변화에만 의존.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    const kind = kindFromSpeaker(speaker);
    const moodMeta = resolveMoodMeta(_mood, kind);
    const paragraphs = splitNarrativeParagraphs(text);
    const portraitVisible = kind === "dialogue" && Boolean(speakerPortraitSrc) && !hidePortrait;

    useEffect(() => {
        setHidePortrait(false);
    }, [speakerPortraitSrc, speaker, text]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.35, ease: tokens.easing.decelerate }}
            className={cn("relative rounded-m p-l select-none overflow-hidden", className)}
            style={{
                background: moodMeta.background,
                border: `1px solid ${moodMeta.border}`,
                boxShadow: `inset 0 0 60px ${moodMeta.innerGlow}, 0 18px 46px ${moodMeta.outerGlow}`,
                minHeight: kind === "dialogue" ? 178 : 204,
                backdropFilter: "blur(8px)",
            }}
        >
            {/* 2026-04-23: 좌측 accent 스트립 — Spirytale 언어 통일 (Toast/StoryFlash/Memorial 과 동일). */}
            <div
                aria-hidden
                className="absolute inset-y-0 left-0"
                style={{
                    width: 2,
                    background: `linear-gradient(180deg, ${moodMeta.accent}FF 0%, ${moodMeta.accent}33 100%)`,
                    boxShadow: `0 0 10px ${moodMeta.outerGlow}`,
                }}
            />
            <div
                className="pointer-events-none absolute inset-x-s"
                style={{
                    top: 10,
                    height: 1,
                    background: `linear-gradient(90deg, transparent, ${moodMeta.border}, transparent)`,
                }}
            />
            <div className="mb-s flex items-start justify-between gap-s">
                <div>
                    {/* 2026-04-23: accent dash + uppercase kicker — 다른 Spirytale 카드와 톤 통일. */}
                    <div
                        className="font-body flex items-center gap-xs"
                        style={{
                            color: moodMeta.accent,
                            fontSize: 10,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            opacity: 0.85,
                            marginBottom: speaker ? 2 : 0,
                        }}
                    >
                        <span
                            aria-hidden
                            style={{
                                width: 10,
                                height: 1,
                                background: moodMeta.accent,
                                boxShadow: `0 0 6px ${moodMeta.outerGlow}`,
                                opacity: 0.7,
                            }}
                        />
                        {kind === "dialogue" ? "목소리" : "기록"}
                    </div>
                    {speaker ? (
                        <div
                            className="font-display"
                            style={{
                                color: moodMeta.speaker,
                                fontSize: tokens.type.size.small,
                                letterSpacing: tokens.type.letterSpacing.wide,
                                textShadow: `0 0 16px ${moodMeta.outerGlow}`,
                            }}
                        >
                            {speaker}
                        </div>
                    ) : null}
                </div>
                {moodMeta.label && (
                    <div
                        className="rounded-pill px-s py-[3px] font-body"
                        style={{
                            color: moodMeta.accent,
                            border: `1px solid ${moodMeta.border}`,
                            background: moodMeta.tagBackground,
                            fontSize: tokens.type.size.tiny,
                            letterSpacing: tokens.type.letterSpacing.wide,
                            boxShadow: `0 0 18px ${moodMeta.outerGlow}`,
                        }}
                    >
                        {moodMeta.label}
                    </div>
                )}
            </div>
            <div
                style={{
                    display: "grid",
                    gap: tokens.spacing.m,
                    gridTemplateColumns: portraitVisible ? "112px minmax(0, 1fr)" : "minmax(0, 1fr)",
                    alignItems: "start",
                }}
            >
                {portraitVisible && (
                    <div
                        className="overflow-hidden rounded-m"
                        style={{
                            width: 112,
                            border: `1px solid ${moodMeta.border}`,
                            background: `linear-gradient(180deg, ${moodMeta.tagBackground}, rgba(14,12,24,0.62))`,
                            boxShadow: `0 12px 24px ${moodMeta.outerGlow}`,
                        }}
                    >
                        <img
                            src={speakerPortraitSrc ?? undefined}
                            alt={speaker ? `${speaker} 초상` : "화자 초상"}
                            onError={() => setHidePortrait(true)}
                            className="block h-[148px] w-full"
                            style={{
                                objectFit: "cover",
                                objectPosition: "center top",
                            }}
                        />
                    </div>
                )}
                <div
                    style={{
                        display: "grid",
                        gap: tokens.spacing.s,
                    }}
                >
                    {paragraphs.map((paragraph, paragraphIndex) => (
                        <p
                            key={`${paragraphIndex}-${paragraph.slice(0, 12)}`}
                            style={{
                                fontFamily: tokens.type.body,
                                color: moodMeta.text,
                                fontSize: kind === "dialogue" ? tokens.type.size.body : tokens.type.size.subtitle,
                                lineHeight: kind === "dialogue" ? tokens.type.lineHeight.loose : tokens.type.lineHeight.normal,
                                letterSpacing: tokens.type.letterSpacing.normal,
                                textAlign: kind === "dialogue" ? "left" : "center",
                                wordBreak: "keep-all",
                                overflowWrap: "break-word",
                                textShadow: kind === "dialogue" ? "none" : `0 0 18px ${moodMeta.outerGlow}`,
                                maxWidth: kind === "dialogue" ? "100%" : "92%",
                                margin: kind === "dialogue" ? "0" : "0 auto",
                            }}
                        >
                            {paragraph}
                        </p>
                    ))}
                </div>
            </div>
            <div
                className="pointer-events-none absolute inset-x-s"
                style={{
                    bottom: 10,
                    height: 1,
                    background: `linear-gradient(90deg, transparent, ${moodMeta.border}, transparent)`,
                }}
            />
            {/*
             * mood 는 내부 작성자용 톤 태그 (영문: "marching", "somber", "grim" 등).
             * 플레이어 UI 에 영문 그대로 노출하면 판타지 몰입이 깨지므로 텍스트 렌더 제거.
             * prop 자체는 유지 — 추후 시각 스타일링 (tint / 애니메이션 variant / SFX 연동) 훅으로
             * 쓸 여지 있음.
             */}
        </motion.div>
    );
}

function kindFromSpeaker(speaker?: string | null): "dialogue" | "narration" {
    return speaker ? "dialogue" : "narration";
}

function splitNarrativeParagraphs(text: string): string[] {
    return text
        .split(/\n+/)
        .map(part => part.trim())
        .filter(Boolean);
}

function resolveMoodMeta(mood: string | undefined, kind: "dialogue" | "narration") {
    const base = {
        text: tokens.color.parchment,
        speaker: kind === "dialogue" ? tokens.color.ember : tokens.color.parchment,
        accent: tokens.color.parchmentDim,
        border: `${tokens.color.parchmentDim}66`,
        innerGlow: `${tokens.color.burgundy}1E`,
        outerGlow: `${tokens.color.ink}66`,
        background: `linear-gradient(180deg, ${tokens.color.ink}F0, ${tokens.color.ink}D8)`,
        tagBackground: "rgba(14,12,24,0.48)",
        label: kind === "dialogue" ? "인물의 말" : "기록된 장면",
    };

    switch (mood) {
        case "marching":
        case "military":
        case "warfront":
        case "briefing":
            return {
                ...base,
                accent: tokens.color.steel,
                speaker: tokens.color.brass,
                border: `${tokens.color.steel}88`,
                innerGlow: `${tokens.color.steel}22`,
                outerGlow: `${tokens.color.steel}33`,
                background: "linear-gradient(180deg, rgba(18,24,34,0.96), rgba(14,12,24,0.92))",
                tagBackground: "rgba(74,85,104,0.18)",
                label: "행군의 공기",
            };
        case "somber":
        case "mourning":
        case "quiet":
        case "closing":
        case "hushed":
            return {
                ...base,
                accent: tokens.color.burgundy,
                speaker: tokens.color.parchmentDim,
                border: `${tokens.color.burgundy}88`,
                innerGlow: `${tokens.color.burgundy}2A`,
                outerGlow: `${tokens.color.burgundy}33`,
                background: "linear-gradient(180deg, rgba(34,18,24,0.96), rgba(14,12,24,0.94))",
                tagBackground: "rgba(92,31,46,0.18)",
                label: "가라앉은 이름들",
            };
        case "decisive":
        case "weighing":
        case "urgent":
        case "duty":
        case "pressure":
            return {
                ...base,
                accent: tokens.color.ember,
                speaker: tokens.color.ember,
                border: `${tokens.color.ember}88`,
                innerGlow: `${tokens.color.ember}1F`,
                outerGlow: `${tokens.color.ember}2C`,
                background: "linear-gradient(180deg, rgba(34,22,18,0.96), rgba(14,12,24,0.92))",
                tagBackground: "rgba(201,106,59,0.14)",
                label: "결단의 순간",
            };
        case "frontier":
        case "uneasy":
        case "haunted":
        case "ominous":
        case "cold":
            return {
                ...base,
                accent: tokens.color.brass,
                speaker: tokens.color.parchmentDim,
                border: `${tokens.color.brass}88`,
                innerGlow: `${tokens.color.brass}1A`,
                outerGlow: `${tokens.color.burgundy}30`,
                background: "linear-gradient(180deg, rgba(28,24,18,0.96), rgba(14,12,24,0.93))",
                tagBackground: "rgba(169,123,61,0.14)",
                label: "변경의 기척",
            };
        default:
            return {
                ...base,
                label: kind === "dialogue" ? "인물의 말" : "기록된 장면",
            };
    }
}
